import { useState, useEffect } from 'react';
import { api, invalidateCached } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

export default function AdminContactEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/content/contact_info').then(r => setContent(r.data)).catch(() => toast.error('Failed to load'));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/content/contact_info', content);
      invalidateCached('/content/contact_info');
      toast.success('Contact info saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!content) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#1E4FA1] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-contact-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Contact Information
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">Update the contact details shown on the public website</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Phone Number</label>
          <Input
            data-testid="contact-phone-input"
            value={content.phone}
            onChange={(e) => setContent({ ...content, phone: e.target.value })}
            placeholder="+1 (555) 123-4567"
            className="rounded-xl"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Email Address</label>
          <Input
            data-testid="contact-email-input-admin"
            value={content.email}
            onChange={(e) => setContent({ ...content, email: e.target.value })}
            placeholder="info@example.org"
            className="rounded-xl"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Physical Address</label>
          <Textarea
            data-testid="contact-address-input"
            value={content.address}
            onChange={(e) => setContent({ ...content, address: e.target.value })}
            placeholder="123 Main St, City, State, ZIP"
            className="rounded-xl"
            rows={3}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Google Maps Embed URL</label>
          <Input
            data-testid="contact-map-input"
            value={content.map_embed_url}
            onChange={(e) => setContent({ ...content, map_embed_url: e.target.value })}
            placeholder="https://www.google.com/maps/embed?pb=..."
            className="rounded-xl"
          />
          <p className="text-xs text-[#94A3B8] mt-1">
            Paste the embed URL from Google Maps (Share &gt; Embed a map &gt; copy the src URL)
          </p>
        </div>

        <div className="flex justify-end pt-4">
          <Button data-testid="save-contact-info-btn" onClick={handleSave} disabled={saving}
            className="bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl px-6">
            <Save size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save Contact Info'}
          </Button>
        </div>
      </div>
    </div>
  );
}
